import { el, triggerDownload, triggerView } from "./dom";
import { iconLock } from "./icons";
import { renderDropzone, formatBytes } from "./dropzone";
import { showToast } from "./toast";
import type { CryptoWorkerClient } from "../lib/workerClient";
import { downloadBlob, type ApiConfig } from "../lib/api";
import { apiTokenState } from "../lib/apiToken";
import { humanizeDecryptFileError, humanizeDecryptFileForRecipientError, humanizeGeneric } from "../lib/humanizeError";
import { hexToBytes } from "../lib/bytes";
import { guessMimeType } from "../lib/mimeGuess";
import { renderSnippetTabs } from "./codeSnippets";

const DECRYPT_FILE_SNIPPETS = {
  sdk: `import { decryptFilePrepare, decryptFileForRecipientPrepare } from "secure-crypt-sdk";

// Password-slot container:
const handle = await decryptFilePrepare(containerStream, somePasswordBytes);

// Or one shared for your public key (needs the envelope too):
const handle = await decryptFileForRecipientPrepare(
  containerStream, envelope, myPrivateKey, myPublicKey,
);
for await (const chunk of handle.chunks()) { /* ... */ }`,
  cli: `# Password slots:
python -m cli.main decrypt-file --in report.enc --out report.pdf

# Or shared for your public key:
python -m cli.main open-shared-file --in report.enc \\
  --envelope report.envelope --privkey my_private.key --out report.pdf`,
  http: `# Optional: fetch the container from the relay by its blob_id first --
# see the Service API page.
curl http://localhost:8000/blobs/<blob_id> -H "X-Api-Key: <key>" -o report.enc`,
};

export function renderDecryptFilePanel(worker: CryptoWorkerClient, apiConfig: ApiConfig, requestToken: () => Promise<void>): HTMLElement {
  let fetchedBlob: Blob | null = null;
  let lastViewUrl: string | null = null;

  const dz = renderDropzone({
    id: "dec-file-input",
    emptyTitle: "No file chosen yet",
    emptyMeta: "Choose a sealed file, or fetch one by its blob_id below.",
    onFileChange: (f) => {
      if (f) dz.setDisplay(f.name, `${formatBytes(f.size)} · ready to decrypt`);
      else dz.reset();
    },
  });
  const fileInput = dz.fileInput;

  const blobIdInput = el("input", { type: "text", placeholder: "e.g. 7c3b3fd8fb734d6e…", id: "dec-file-blobid", class: "mono-field" });
  const fetchBtn = el("button", { type: "button", class: "secondary-btn" }, ["Fetch from service"]);
  const passwordInput = el("input", { type: "password", id: "dec-file-password", autocomplete: "current-password", placeholder: "Any one of the passwords that unlocks it" });
  const envelopeInput = el("textarea", {
    id: "dec-file-envelope",
    rows: "2",
    class: "mono-field",
    placeholder: "Paste the envelope (hex) here instead, if this file was shared with your public key",
  });
  const decryptBtn = el("button", { type: "button", class: "primary-btn" }, [iconLock(), "Decrypt file"]);
  const progress = el("progress", { max: "100", value: "0" }, []);
  progress.style.display = "none";
  // The save-to-disk control is persistent, not a toast - only the
  // success/failure events themselves are transient.
  const resultCard = el("div", {}, []);

  fetchBtn.addEventListener("click", async () => {
    // Needs an API token - request one automatically if we don't have one yet.
    if (!apiTokenState.token) {
      await requestToken();
      if (!apiTokenState.token) return; // requestToken already surfaced the error
    }
    const token = apiTokenState.token;
    try {
      fetchedBlob = await downloadBlob(apiConfig, blobIdInput.value.trim(), token);
      dz.setDisplay(
        `Fetched from service (${blobIdInput.value.trim().slice(0, 12)}…)`,
        `${formatBytes(fetchedBlob.size)} · ready to decrypt`,
      );
      showToast(`Fetched ${formatBytes(fetchedBlob.size)}. Enter a password and decrypt.`, "success");
    } catch (e) {
      showToast(humanizeGeneric(e, "Couldn't fetch that file from the service."), "error");
    }
  });

  function buildResultActions(filename: string, blob: Blob, label: string | null) {
    resultCard.innerHTML = "";
    if (lastViewUrl) {
      URL.revokeObjectURL(lastViewUrl);
      lastViewUrl = null;
    }
    showToast(label ? `Unsealed - matched slot "${label}".` : "Unsealed.", "success");

    const downloadResultBtn = el("button", { type: "button", class: "secondary-btn" }, ["Save decrypted file"]);
    downloadResultBtn.addEventListener("click", () => triggerDownload(filename, blob));

    const viewBtn = el("button", { type: "button", class: "secondary-btn" }, ["View"]);
    viewBtn.addEventListener("click", () => {
      if (lastViewUrl) URL.revokeObjectURL(lastViewUrl);
      const typedBlob = new Blob([blob], { type: guessMimeType(filename) });
      lastViewUrl = triggerView(typedBlob);
    });

    resultCard.append(
      el("div", { class: "actions" }, [downloadResultBtn, viewBtn]),
      el("p", { class: "trust-note" }, [
        "\"View\" opens it in a new tab without saving a decrypted copy through this app -- it doesn't prevent the file being saved by other means once it's open.",
      ]),
    );
  }

  decryptBtn.addEventListener("click", async () => {
    resultCard.innerHTML = "";
    const file = fileInput.files?.[0] ?? fetchedBlob;
    if (!file) {
      showToast("Choose a file, or fetch one from the service first.", "error");
      return;
    }
    const filename = file instanceof File ? file.name : "decrypted.out";
    const envelopeHex = envelopeInput.value.trim();

    progress.style.display = "";
    progress.value = 0;
    try {
      if (envelopeHex.length > 0) {
        let envelope: Uint8Array;
        try {
          envelope = hexToBytes(envelopeHex);
        } catch {
          showToast("That envelope doesn't look like valid hex.", "error");
          return;
        }
        const { blob } = await worker.decryptFileForRecipient(file, envelope, (processed, total) => {
          progress.value = total > 0 ? Math.round((processed / total) * 100) : 0;
        });
        buildResultActions(filename, blob, null);
      } else {
        if (passwordInput.value.length === 0) {
          showToast("Enter the password, or paste an envelope above instead.", "error");
          return;
        }
        const password = new TextEncoder().encode(passwordInput.value);
        passwordInput.value = "";
        const { label, blob } = await worker.decryptFile(file, password, (processed, total) => {
          progress.value = total > 0 ? Math.round((processed / total) * 100) : 0;
        });
        buildResultActions(filename, blob, label);
      }
    } catch (e) {
      showToast(envelopeHex.length > 0 ? humanizeDecryptFileForRecipientError(e) : humanizeDecryptFileError(e), "error");
    } finally {
      progress.style.display = "none";
    }
  });

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Files · Decrypt"]),
    el("h2", {}, ["Open a file with one of its passwords"]),
    el("p", { class: "lede" }, [
      "Any single password that was used to seal this file will open it. You don't need the others. Shared with your public key instead? Paste the envelope you were given below.",
    ]),
    el("p", { class: "section-label" }, ["Integration"]),
    renderSnippetTabs(DECRYPT_FILE_SNIPPETS),
    el("p", { class: "section-label" }, ["Try it live"]),
    el("div", { class: "card" }, [
      dz.element,
      el("p", { class: "section-label" }, ["...or fetch it from the service"]),
      el("p", { class: "section-sub" }, ["Paste the blob_id you were given when the file was uploaded."]),
      blobIdInput,
      el("div", { class: "actions" }, [fetchBtn]),
      el("p", { class: "field-label" }, ["Password"]),
      passwordInput,
      el("p", { class: "field-label" }, ["Envelope (only if this was shared with your public key)"]),
      envelopeInput,
      el("div", { class: "divider" }, []),
      decryptBtn,
      progress,
      resultCard,
    ]),
  ]);
}
